import { useAccount } from '@azure/msal-react';
import useFetchCurrentUser from '@components/features/currentUser/hooks/useFetchCurrentUser';
import { buildVersion } from '@constants/buildVersion';
import useFetchVersion from '@hooks/useFetchVersion';
import { FC, ReactNode, useEffect, useRef } from 'react';
import { useCookies } from 'react-cookie';
import { format } from 'date-fns';
import useFeatureFlags from '@hooks/useFeatureFlags';
import Button from '@components/primitives/button/Button';
import { BsPower } from 'react-icons/bs';
import useTraceShell from './hooks/useTraceShell';
import NetworkErrorDisplay from './components/NetworkErrorDisplay';
import ConsoleErrorDisplay from './components/ConsoleErrorDisplay';
import { usePrefersColorScheme } from './hooks/usePrefersColorScheme';
import { useAtomValue } from 'jotai';
import { themeAtom } from '@js/store/settingsAtoms';
import { useParams } from 'react-router-dom';
import useFetchDocument from '@hooks/useFetchDocument';
import useDocumentStatus from '@hooks/useDocumentStatus';
import useTrackEvent from '@hooks/useTrackEvent';
import useIsOutdatedBrowser from '@components/features/outdatedBrowser/hooks/useIsOutdatedBrowser';
import useFetchDiagnostics from '@hooks/useFetchDiagnostics';
import { NumericFormat } from 'react-number-format';
import { getCurrentLocale } from '@utils/locales';

interface TraceShellProps {
    children: React.ReactNode;
}

const TraceShell: FC<TraceShellProps> = ({ children }) => {
    const { isTraceShellEnabled } = useTraceShell();
    if (!isTraceShellEnabled) return children;
    return <ActiveTraceShell>{children}</ActiveTraceShell>;
};

export default TraceShell;

const ActiveTraceShell: FC<TraceShellProps> = ({ children }) => {
    const msalAccount = useAccount();
    const { data: user } = useFetchCurrentUser();
    const { data: versionData } = useFetchVersion();
    const { data: diagnostics } = useFetchDiagnostics();
    const currentLocale = getCurrentLocale();
    const [cookies] = useCookies();
    const featureFlagsData = useFeatureFlags();
    const { clockRef } = useLiveClock();
    const { disableTraceShell } = useTraceShell();
    const osTheme = usePrefersColorScheme();
    const theme = useAtomValue(themeAtom);
    const { documentId } = useParams();
    const { isEditMode } = useDocumentStatus();
    const { data: document, error } = useFetchDocument(+documentId, isEditMode);
    const { getCustomDimensions } = useTrackEvent();
    const isOutdatedBrowser = useIsOutdatedBrowser();

    const currentTime = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
    const hublCookieVersion = cookies?.clientversion || 'onbekend';
    const hublApiVersion = versionData?.version || 'onbekend';
    const docbaseVersion = versionData?.docbaseVersion || 'onbekend';
    const msNameOfUser = msalAccount?.name || 'onbekend';
    const wvNameOfUser = user?.userfullname || 'onbekend';
    const wvUsername = user?.userId || 'onbekend';
    const wvUserType = user?.userType || 'onbekend';
    const wvSsoUser = user?.email || 'onbekend';
    const upn = msalAccount?.username || 'onbekend';
    const tenantId = user?.tenantId || 'onbekend';
    const tenantName = user?.tenantName || 'onbekend';
    const erp = user?.erp || 'onbekend';
    const userAgent = navigator.userAgent;
    const activeFeatureFlags = featureFlagsData ? Object.entries(featureFlagsData).filter(([, value]) => value === true) : [];
    const activeFeatureFlagKeys = activeFeatureFlags.map(([key]) => key).join(', ') || 'Geen actieve feature flags';
    const customDimensionsObject = getCustomDimensions(document?.documentId || 0);
    const customDimensions =
        Object.entries(customDimensionsObject)
            .map(([key, value]) => `${key}: ${value.value}`)
            .join(', ') || '-';
    const ocrTrainingAllowed = user?.allowTraining || false;
    const ocrTrainingEnabled = ocrTrainingAllowed && document?.meta.trainingEnabled;

    const applicationInfo = {
        'MS user': `${msNameOfUser} (${upn})`,
        'WV user': `${wvNameOfUser} (${wvUsername}, type: ${wvUserType}), sso: ${wvSsoUser}`,
        'Hubl client version': `${buildVersion || 'unknown'} (${hublCookieVersion})`,
        'Hubl API version': hublApiVersion,
        'Docbase version': docbaseVersion,
        Tenant: `${tenantName} (${tenantId})`,
        ERP: erp,
        'Enabled feature flags': activeFeatureFlagKeys,
        'Application theme': theme,
    };

    const systemInfo = {
        Datetime: <span ref={clockRef}>{currentTime}</span>,
        'User Agent': userAgent,
        'Device Pixel Ratio': window.devicePixelRatio || 1,
        'Screen resolution': `${window.screen.width} x ${window.screen.height}`,
        'OS theme': osTheme,
        'Outdated browser': isOutdatedBrowser ? 'yes' : 'no',
        'Network error requests': <NetworkErrorDisplay />,
        'Console errors': <ConsoleErrorDisplay />,
    };

    const documentInfo = {
        'Document ID': document?.documentId || 'unknown',
        'Workflow name': document?.workflowName || 'unknown',
        'Workflow state name': document?.workflowStateName || 'unknown',
        'Document error': error ? error.message : 'none',
        'Custom dimensions': customDimensions,
        'OCR training allowed': ocrTrainingAllowed ? 'yes' : 'no',
        'OCR training enabled': ocrTrainingEnabled ? 'yes' : 'no',
    };

    const formattingInfo = {
        Amount: (
            <>
                Server:
                <NumericFormat
                    prefix={` ${diagnostics?.diagnostics.currencySymbol}`}
                    displayType="text"
                    value={1234.56}
                    thousandSeparator={diagnostics?.diagnostics.currencyGroupSeparator}
                    decimalSeparator={diagnostics?.diagnostics.currencyDecimalSeparator}
                />
                <br />
                Front-end:
                <NumericFormat
                    prefix={` `}
                    displayType="text"
                    value={1234.56}
                    thousandSeparator={currentLocale.thousandSeparator}
                    decimalSeparator={currentLocale.decimalSeparator}
                />
            </>
        ),
        Number: (
            <>
                Server:
                <NumericFormat
                    prefix={' '}
                    displayType="text"
                    value={1234.56}
                    thousandSeparator={diagnostics?.diagnostics.numberGroupSeparator}
                    decimalSeparator={diagnostics?.diagnostics.numberDecimalSeparator}
                />
                <br />
                Front-end:
                <NumericFormat
                    prefix={` `}
                    displayType="text"
                    value={1234.56}
                    thousandSeparator={currentLocale.thousandSeparator}
                    decimalSeparator={currentLocale.decimalSeparator}
                />
            </>
        ),
        Date: (
            <>
                Server: {diagnostics?.diagnostics.shortDatePattern} {diagnostics?.diagnostics.longTimePattern}
                <br />
                Front-end: {currentLocale.dateTimeAccurateFormat}
            </>
        ),
        Timezone: (
            <>
                Server: {diagnostics?.diagnostics.timeZone}
                <br />
                Front-end: {Intl.DateTimeFormat()?.resolvedOptions()?.timeZone ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'unknown'}
            </>
        ),
    };

    return (
        <div className="flex flex-row items-stretch justify-between fixed inset-0">
            <div className="w-full max-w-[calc(100vw-350px)] flex-1">{children}</div>

            <div className="w-[350px] h-full relative border-0 border-l-2 border-solid border-gray-500 text-gray-100 font-mono font-bold bg-black p-4 text-base overflow-y-scroll">
                <div className="w-full break-all grid grid-cols-1 gap-2">
                    <SectionHeading title="Application" />
                    {Object.entries(applicationInfo).map(([label, value]) => (
                        <LabelValuePair key={label} label={label} value={value} />
                    ))}

                    <SectionHeading title="Document context" />
                    {Object.entries(documentInfo).map(([key, value]) => (
                        <LabelValuePair key={key} label={key} value={value} />
                    ))}

                    <SectionHeading title="System" />
                    {Object.entries(systemInfo).map(([key, value]) => (
                        <LabelValuePair key={key} label={key} value={value} />
                    ))}

                    <SectionHeading title="Formatting" />
                    {Object.entries(formattingInfo).map(([key, value]) => (
                        <LabelValuePair key={key} label={key} value={value} />
                    ))}
                </div>
                <span className="grid grid-cols-1 gap-4 mt-4">
                    <Button icon={<BsPower strokeWidth={1} />} primary onClick={disableTraceShell} className="w-full font-black">
                        Turn off tracing
                    </Button>
                </span>
            </div>
        </div>
    );
};

const SectionHeading: FC<{ title: string }> = ({ title }) => <span className="mr-auto font-black uppercase tracking-widest bg-white text-black px-1 rounded-md">{title}</span>;

const LabelValuePair = ({ label, value }: { label: string; value: ReactNode | string }) => (
    <span className="w-full text-right">
        <span className="inline-flex text-left float-left pr-8">
            <u>{label}</u>:
        </span>
        {value}
    </span>
);

const useLiveClock = () => {
    const clockRef = useRef<HTMLSpanElement | null>(null);
    const updateClock = () => {
        if (clockRef.current && typeof clockRef.current['textContent'] === 'string') {
            clockRef.current.textContent = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
        }
    };

    useEffect(() => {
        if (!clockRef.current) return;
        const interval = setInterval(updateClock, 1000);

        return () => clearInterval(interval);
    }, [clockRef]);

    return {
        clockRef,
    };
};
